import {
  DestroyRef,
  Injectable,
  type InjectionToken,
  inject,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  MSAL_GUARD_CONFIG,
  MsalBroadcastService,
  type MsalGuardAuthRequest,
  type MsalGuardConfiguration,
  MsalService,
} from '@azure/msal-angular';
import {
  type AccountInfo,
  type AuthenticationResult,
  type EventMessage,
  EventType,
  InteractionStatus,
  type RedirectRequest,
} from '@azure/msal-browser';
import {
  AuthStore,
  type IAuthService,
} from '@clean-code/shared/auth/util-auth';
import { firstValueFrom, type Observable } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { AUTH_CONFIG } from '../model/auth-config.token';

/**
 * msal-angular declares MSAL_GUARD_CONFIG as `InjectionToken<string>`, so the
 * injected value has to be narrowed to the shape it actually provides.
 */
const MSAL_GUARD_CONFIG_TOKEN =
  MSAL_GUARD_CONFIG as unknown as InjectionToken<MsalGuardConfiguration>;

/** Events that carry a freshly issued access token. */
const TOKEN_SUCCESS_EVENTS: EventType[] = [
  EventType.LOGIN_SUCCESS,
  EventType.ACQUIRE_TOKEN_SUCCESS,
  EventType.SSO_SILENT_SUCCESS,
];

/**
 * Statuses during which MSAL rejects a new interactive request with
 * `interaction_in_progress`.
 */
const BUSY_INTERACTION_STATUSES: InteractionStatus[] = [
  InteractionStatus.Login,
  InteractionStatus.Logout,
  InteractionStatus.AcquireToken,
  InteractionStatus.SsoSilent,
  InteractionStatus.HandleRedirect,
];

@Injectable()
export class AzureAuthService implements IAuthService {
  #authStore = inject(AuthStore);
  #destroyRef = inject(DestroyRef);
  #authService = inject(MsalService);
  #msalGuardConfig = inject(MSAL_GUARD_CONFIG_TOKEN);
  #config = inject(AUTH_CONFIG, { optional: true });
  #interactionStatus: InteractionStatus = InteractionStatus.Startup;

  constructor() {
    const msalBroadcastService = inject(MsalBroadcastService);

    msalBroadcastService.msalSubject$
      .pipe(
        filter((msg: EventMessage) =>
          TOKEN_SUCCESS_EVENTS.includes(msg.eventType),
        ),
        map((msg: EventMessage) => msg.payload as AuthenticationResult | null),
        filter(
          (result): result is AuthenticationResult => !!result?.accessToken,
        ),
        tap((result: AuthenticationResult) => this.onTokenAcquired(result)),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe();

    msalBroadcastService.inProgress$
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe((status: InteractionStatus) => {
        this.#interactionStatus = status;
      });
  }

  hasValidIdToken(): boolean {
    throw new Error('Method not implemented.');
  }

  hasValidAccessToken(): boolean {
    throw new Error('Method not implemented.');
  }

  login(): void {
    if (this.isInteractionInProgress()) {
      return;
    }

    const authRequest = {
      ...this.resolveGuardAuthRequest(),
      // /login is outside MsalGuard; returning there after Azure leaves the
      // session established but the user stuck on the login screen.
      redirectStartPage: `${window.location.origin}/`,
    } as RedirectRequest;

    // MsalService wraps an already started promise, so it must be subscribed
    // to surface rejections instead of leaking an unhandled rejection.
    this.#authService
      .loginRedirect(authRequest)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: (error: unknown) =>
          console.error('MSAL loginRedirect failed', error),
      });
  }

  logout(): void {
    this.#authStore.reset();

    if (this.isInteractionInProgress()) {
      return;
    }

    this.#authService
      .logoutRedirect()
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        error: (error: unknown) =>
          console.error('MSAL logoutRedirect failed', error),
      });
  }

  handleUnauthorized(): void {
    this.#authStore.reset();
    this.login();
  }

  getAccessToken$(): Observable<string> {
    return toObservable(this.#authStore.accessToken);
  }

  async getAccessToken(): Promise<string> {
    const account = this.resolveActiveAccount();
    const scopes = this.apiScopes();

    // Without an account or configured API scopes there is nothing to renew,
    // so fall back to whatever the last successful acquisition produced.
    if (!account || scopes.length === 0) {
      return this.cachedAccessToken();
    }

    try {
      const result = await firstValueFrom(
        this.#authService.acquireTokenSilent({ account, scopes }),
      );

      if (!result?.accessToken) {
        return this.cachedAccessToken();
      }

      this.onTokenAcquired(result);
      return result.accessToken;
    } catch (error) {
      console.error('MSAL acquireTokenSilent failed', error);
      return this.cachedAccessToken();
    }
  }

  private onTokenAcquired(result: AuthenticationResult): void {
    // Keep MSAL's active account in sync, otherwise silent renewal fails once
    // the redirect that established the session is gone.
    if (result.account) {
      this.#authService.instance.setActiveAccount(result.account);
    }

    this.#authStore.setAccessToken(result.accessToken);
  }

  private cachedAccessToken(): string {
    const accessToken = this.#authStore.accessToken();

    if (!accessToken) {
      throw new Error('no access token');
    }

    return accessToken;
  }

  private resolveActiveAccount(): AccountInfo | null {
    const activeAccount = this.#authService.instance.getActiveAccount();

    if (activeAccount) {
      return activeAccount;
    }

    const [account] = this.#authService.instance.getAllAccounts();

    if (account) {
      this.#authService.instance.setActiveAccount(account);
      return account;
    }

    return null;
  }

  /** Scopes of the protected backend, which differ from the login scopes. */
  private apiScopes(): string[] {
    const resourceMap = this.#config?.protectedResourceMap;

    if (!resourceMap) {
      return [];
    }

    for (const scopes of resourceMap.values()) {
      if (scopes?.length) {
        return scopes;
      }
    }

    return [];
  }

  private resolveGuardAuthRequest(): MsalGuardAuthRequest {
    const authRequest = this.#msalGuardConfig.authRequest;

    if (typeof authRequest === 'function') {
      // The router state is only supplied by MsalGuard; a manual login has none.
      const factory = authRequest as (
        authService: MsalService,
      ) => MsalGuardAuthRequest;
      return { ...factory(this.#authService) };
    }

    return { ...authRequest };
  }

  private isInteractionInProgress(): boolean {
    return BUSY_INTERACTION_STATUSES.includes(this.#interactionStatus);
  }
}
